import React, { useState, useContext } from "react";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";

const CreateRoleForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    role: "",
    role_description: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiBase = getApiBaseURL();
  const { user } = useContext(AuthContext);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.role.trim()) {
      setError("Role name is required");
      return;
    }

    // Check for reserved role names
    const reservedRoles = ["admin", "super_admin", "system"];
    if (reservedRoles.includes(formData.role.toLowerCase())) {
      setError("This role name is reserved by the system");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // HIPAA Compliant: Using secureAxios with httpOnly cookies
      await secureAxios.post(`${apiBase}/api/roles`, {
        role: formData.role.trim(),
        role_description: formData.role_description.trim(),
      });

      alert("✅ Role created successfully");
      setFormData({ role: "", role_description: "" });

      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("❌ Error creating role:", err);
      setError(err.response?.data?.error || "Failed to create role");
    } finally {
      setLoading(false);
    }
  };

  // Check authorization
  if (user?.role !== "admin") {
    return (
      <div className="alert alert-info" role="alert">
        Only administrators can create roles.
      </div>
    );
  }

  return (
    <div>
      <h4>Create New Role</h4>

      {error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="row g-3">
        <div className="col-md-6">
          <label htmlFor="role-name" className="form-label">
            Role Name <span className="text-danger">*</span>
          </label>
          <input
            id="role-name"
            name="role"
            className="form-control"
            value={formData.role}
            onChange={handleChange}
            required
            maxLength={50}
            placeholder="e.g., manager, viewer"
            disabled={loading}
          />
          <div className="form-text">
            Use lowercase, no spaces (e.g., "sales_manager")
          </div>
        </div>

        <div className="col-12">
          <label htmlFor="role-description" className="form-label">
            Description
          </label>
          <textarea
            id="role-description"
            name="role_description"
            className="form-control"
            value={formData.role_description}
            onChange={handleChange}
            rows="3"
            maxLength={500}
            placeholder="Describe the permissions and responsibilities of this role"
            disabled={loading}
          />
          <div className="form-text">
            {formData.role_description.length}/500 characters
          </div>
        </div>

        <div className="col-12">
          <button
            type="submit"
            className="btn btn-success"
            disabled={loading || !formData.role.trim()}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Creating...
              </>
            ) : (
              "Create Role"
            )}
          </button>

          <button
            type="button"
            className="btn btn-secondary ms-2"
            onClick={() => {
              setFormData({ role: "", role_description: "" });
              setError(null);
            }}
            disabled={loading}
          >
            Clear
          </button>
        </div>
      </form>

      {/* Security Notice */}
      <div className="alert alert-warning mt-3" role="alert">
        <i className="bi bi-exclamation-triangle"></i>{" "}
        <strong>Security Notice:</strong>
        Creating new roles affects system access control. All role changes are
        logged for security audit.
      </div>
    </div>
  );
};

export default CreateRoleForm;
